import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { debounce } from 'lodash';
import { Link } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import {
  allEventsQueryAtom,
  eventKolsAtom,
  filteredEventKolsAtom,
  kolListFiltersAtom,
  NEW_KOL_PERIOD_DAYS,
  type KolSortKey,
} from 'r/atoms';
import { Avatar, SpinnerCircle } from 'r/ui';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import PaginationControls from '../AnalysisManagerTab/PaginationControls';
import {
  formatDate,
  formatHitRate,
  HitMissCounts,
  NewKolBadge,
} from './shared';

const SORT_OPTIONS: { value: KolSortKey; label: string }[] = [
  { value: 'firstEventAt_desc', label: 'First event (Newest)' },
  { value: 'lastEventAt_desc', label: 'Last event (Newest)' },
  { value: 'eventsCount_desc', label: 'Events (High → Low)' },
  { value: 'hitRate_desc', label: 'Hit rate (High → Low)' },
  { value: 'username_asc', label: 'Username (A → Z)' },
];

export default function KolsTab() {
  const [filters, setFilters] = useAtom(kolListFiltersAtom);
  const eventsQuery = useAtomValue(allEventsQueryAtom);
  const allKols = useAtomValue(eventKolsAtom);
  const kols = useAtomValue(filteredEventKolsAtom);

  const [localSearch, setLocalSearch] = useState(filters.search);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((search: string) => {
        setFilters((prev) => ({ ...prev, search, page: 0 }));
      }, 400),
    [setFilters],
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setLocalSearch(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch],
  );

  const handleSearchClear = () => {
    debouncedSetSearch.cancel();
    setLocalSearch('');
    setFilters((prev) => ({ ...prev, search: '', page: 0 }));
  };

  const newCount = allKols?.filter((k) => k.isNew).length ?? 0;
  const total = kols?.length ?? 0;
  const pageItems =
    kols?.slice(
      filters.page * filters.limit,
      (filters.page + 1) * filters.limit,
    ) ?? [];

  if (eventsQuery.isError) {
    return (
      <div className="py-8 text-center text-secondary">
        Failed to load events.{' '}
        <Button variant="link" onClick={() => eventsQuery.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!kols) {
    return (
      <div className="flex justify-center p-10">
        <SpinnerCircle size={24} color="var(--muted-foreground)" />
      </div>
    );
  }

  return (
    <div className="w-full bg-background rounded-2xl p-6 shadow-sm border border-bg-accent-tabs">
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 mb-6">
        <div>
          <h2 className="text-[20px] text-main font-medium">KOLs</h2>
          <p className="text-sm text-secondary">
            Authors with at least one SanPlay event. New = first event within{' '}
            {NEW_KOL_PERIOD_DAYS} days.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full xl:w-auto">
          <div className="relative sm:w-64">
            <Input
              aria-label="Search KOL"
              placeholder="Search by username or name..."
              value={localSearch}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pr-14"
            />
            <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            {localSearch && (
              <button
                onClick={handleSearchClear}
                aria-label="Clear search"
                className="absolute right-8 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <Select
            value={filters.sortBy}
            onValueChange={(value) =>
              setFilters((prev) => ({
                ...prev,
                sortBy: value as KolSortKey,
                page: 0,
              }))
            }
          >
            <SelectTrigger className="sm:w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <Checkbox
              id="kols-only-new"
              checked={filters.onlyNew}
              onCheckedChange={(checked) =>
                setFilters((prev) => ({
                  ...prev,
                  onlyNew: checked === true,
                  page: 0,
                }))
              }
            />
            <Label htmlFor="kols-only-new" className="text-sm text-secondary">
              Only new ({newCount})
            </Label>
          </div>
        </div>
      </div>

      <div className="text-sm text-secondary mb-4 font-medium">
        {total} KOL{total !== 1 ? 's' : ''}
        {eventsQuery.isFetching && (
          <span className="ml-2 text-blue-500 animate-pulse">Updating...</span>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-left text-sm">
          <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
            <tr>
              <th className="py-3 px-4 font-medium">KOL</th>
              <th className="py-3 px-4 font-medium">Events</th>
              <th className="py-3 px-4 font-medium">Active</th>
              <th
                className="py-3 px-4 font-medium"
                title="Hit / Miss / Neutral"
              >
                H / M / N
              </th>
              <th className="py-3 px-4 font-medium">Hit rate</th>
              <th className="py-3 px-4 font-medium">First event</th>
              <th className="py-3 px-4 font-medium">Last event</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((kol) => (
              <tr
                key={kol.kolID}
                className="border-b hover:bg-muted/50 transition-colors"
              >
                <td className="py-3 px-4">
                  <Link
                    to={`/kols/${kol.kolID}`}
                    className="flex items-center gap-3 group"
                  >
                    <Avatar
                      size={32}
                      user={{ id: kol.username, avatar: kol.avatar }}
                    />
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium text-main group-hover:underline truncate">
                        {kol.name || kol.username}
                      </span>
                      <span className="text-xs text-muted-foreground truncate">
                        @{kol.username}
                      </span>
                    </div>
                    {kol.isNew && <NewKolBadge />}
                  </Link>
                </td>
                <td className="py-3 px-4">{kol.eventsCount}</td>
                <td className="py-3 px-4">{kol.activeCount}</td>
                <td className="py-3 px-4">
                  <HitMissCounts
                    hits={kol.hits}
                    misses={kol.misses}
                    neutrals={kol.neutrals}
                  />
                </td>
                <td className="py-3 px-4">{formatHitRate(kol.hitRate)}</td>
                <td className="py-3 px-4 text-muted-foreground">
                  {formatDate(kol.firstEventAt)}
                </td>
                <td className="py-3 px-4 text-muted-foreground">
                  {formatDate(kol.lastEventAt)}
                </td>
              </tr>
            ))}
            {pageItems.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="py-8 text-center text-muted-foreground"
                >
                  No KOLs found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <PaginationControls
        key={filters.page}
        page={filters.page}
        total={total}
        limit={filters.limit}
        limitOptions={[25, 50, 100]}
        onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
        onLimitChange={(limit) =>
          setFilters((prev) => ({ ...prev, limit, page: 0 }))
        }
      />
    </div>
  );
}
